
import React from "react";
import { Capability, Schemes, Utils } from "../../../../utils";
import { i18nManager } from "../../../../i18n";
import * as ReactRouterDom from "react-router-dom";
import LoadingGif from "../../../../assets/images/loading_circle.gif";
import { BaseService, HostService, IGallery, IHost, IHostWebsite, IMember, SanitizedResponse } from "../../../../services";
import { Alignment, Button, NoseurObject, TabPane, TabPanel, useScreenDimension } from "@rivtn/noseur";
import ImportExport from "../../../page_design/ImportExport";
import Gallery from "../../../shared/settings/Gallery";
import Merch from "../../../shared/settings/Merch";
import Design from "./Design";
import SocialNetworks from "./general/SocialNetworks";
import BannerMedias from "./general/BannerMedias";
import WebsitePages from "./general/WebsitePages";
import AppPages from "./general/AppPages";
import HostEarnings from "./general/HostEarnings";
import Managers from "./general/Managers";
import Home from "./Home";
import Tournaments from "./Tournaments";
import Events from "./Events";
import Basic from "./general/Basic";
import ManageTournaments from "./ManageTournaments";
import MobileDesign from "./MobileDesign";
import Visibilities from "./about/Visibilities";
import PhysicalAddress from "./about/PhysicalAddress";
import EmailPhone from "./about/EmailPhone";
import People from "./about/People";
import Categories from "./blog/Categories";
import Articles from "./blog/Articles";
import Comments from "./blog/Comments";
import { Siren } from "../../../shared/ConfirmDialog";
import CompanyInfo from "./about/CompanyInfo";

export interface HostSettingsProps {
    profile: IMember;
}

function HostSettings(props: HostSettingsProps) {

    const { width } = useScreenDimension();
    const hostService = HostService.getInstance();
    const [host, setHost] = React.useState<IHost>();
    const labels = i18nManager.Labels.host.settings;
    let { id: hostId = "" } = ReactRouterDom.useParams();
    const blogTabs = ["CATEGORIES", "ARTICLES", "COMMENTS"];
    const merchRivtnAdStateState = React.useState<boolean>();
    const hostWebsiteHeroState = React.useState<IGallery[]>();
    const galleryRivtnAdStateState = React.useState<boolean>();
    const [profile, _] = React.useState<IMember>(props.profile);
    const [hostWebsite, setHostWebsite] = React.useState<IHostWebsite>();
    const aboutTabs = ["VISIBILITIES", "INFO", "ADDR", "EMAILS_PHONES", "PEOPLE"];
    const [searchParams, setSearchParams] = ReactRouterDom.useSearchParams(window.location.search);
    const genTabs = ["BASIC", "MANAGERS", "HEARNS", "HNETS", "BMEDIAS", "WEBSITE_PAGES", "MOBILE_APPS_FEATURES", "IMEXPORT"];
    const tabs = ["GENERAL", "MANAGE_TOURNAMENTS", "HOME", "HERO", "MERCH", "TOURNAMENTS", "EVENTS", "BLOG", "ABOUT", "WEBSITE", "MOBILE_APP"];

    React.useEffect(fetchInformation, []);

    return (<div className="settings earnings analytics">
        <div className="header">
            <div className="l" style={{ display: "flex", flexDirection: "column" }}>
                <span className="t lined-limit"><i className="fa fa-arrow-left" onClick={() => window.history.back()} />
                    {labels.host} - {hostId}
                </span>
                <span className="lined-limit" style={{ marginLeft: 30, fontWeight: "normal" }}>
                    {host?.name}
                </span>
            </div>
            {searchParams.get("tab") === "MANAGE_TOURNAMENTS"
                && (<Button fill raised scheme={Schemes.OMNEHOST} link={`/hosts/${hostId}/create-tournament`} text={labels.manage_tournaments.create_tournament} />)}
        </div>
        {(!host || !hostWebsite)
            ? (<div style={{ margin: "auto" }}>
                <img alt={"loading"} src={LoadingGif} style={{ width: 80, margin: "auto" }} />
            </div>)
            : (<TabPane renderActiveTabOnly noUnderline idleScheme={Schemes.IDLE} scheme={Schemes.OMNEHOST} activeIndex={tabs.indexOf(searchParams.get("tab") ?? "GENERAL")}
                style={{ marginTop: 10, marginLeft: 10, height: "100%" }} attrsRelay={{ content: { style: { margin: 0, padding: 0, border: "none" } } }} onTabActive={(i) => {
                    Utils.updateSearchParams("tab", tabs[i], setSearchParams);
                }}>
                <TabPanel header={labels.general.title} >
                    <div className="te-settings-gen">
                        <TabPane renderActiveTabOnly noUnderline idleScheme={Schemes.IDLE} scheme={Schemes.OMNEHOST} alignment={width > 800 ? Alignment.LEFT : Alignment.TOP}
                            activeIndex={genTabs.indexOf(searchParams.get("gtab") ?? "BASIC")} style={{ height: "100%", maxWidth: (width > 800 ? "100%" : "90vw") }}
                            attrsRelay={{ navigator: { headers: { style: { borderRight: "none", paddingLeft: (width > 800 ? 0 : 480) } } } }} onTabActive={(i) => {
                                Utils.updateSearchParams("gtab", genTabs[i], setSearchParams);
                            }}>
                            <TabPanel header={labels.general.basic.title} attrsRelay={{ header: { style: { marginTop: (width > 800 ? 30 : 20) } } }}>
                                <Basic host={host} profile={profile} />
                            </TabPanel>
                            <TabPanel header={labels.general.managers.title} attrsRelay={{ header: { style: { marginTop: 20 } } }}>
                                <Managers host={host} profile={profile} />
                            </TabPanel>
                            <TabPanel header={labels.general.host_earnings.title} attrsRelay={{ header: { style: { marginTop: 20 } } }}>
                                <HostEarnings host={host} profile={profile} />
                            </TabPanel>
                            <TabPanel header={labels.general.social_networks.title} attrsRelay={{ header: { style: { marginTop: 20 } } }}>
                                <SocialNetworks host={host} hostWebsite={hostWebsite} profile={profile} />
                            </TabPanel>
                            <TabPanel header={labels.general.banner_medias.title} attrsRelay={{ header: { style: { marginTop: 20 } } }}>
                                <BannerMedias host={host} hostWebsite={hostWebsite} profile={profile} />
                            </TabPanel>
                            <TabPanel header={labels.general.website_pages.title} attrsRelay={{ header: { style: { marginTop: 20 } } }}>
                                <WebsitePages host={host} hostWebsite={hostWebsite} profile={profile} />
                            </TabPanel>
                            <TabPanel header={labels.general.mobile_app_feature.title} attrsRelay={{ header: { style: { marginTop: 20 } } }}>
                                <AppPages host={host} hostWebsite={hostWebsite} profile={profile} />
                            </TabPanel>
                            <TabPanel header={i18nManager.Labels.import_export.title} attrsRelay={{ header: { style: { marginTop: 20 } } }}>
                                <ImportExport copyCapability={Capability.DISABLE_HOST_WEBSITE_SETTINGS_COPYING} importCapability={Capability.IMPORT_HOST_WEBSITE_SETTINGS} exportCapability={Capability.EXPORT_HOST_WEBSITE_SETTINGS} section="host_website" export={() => new Promise((resolve, _) => {
                                    setTimeout(() => {
                                        resolve(i18nManager.Labels.import_export.cannot_import_settings);
                                    }, 2000)
                                })} import={async (settings: NoseurObject<string>) => new Promise((resolve, _) => {
                                    setTimeout(() => {
                                        resolve(i18nManager.Labels.import_export.cannot_import_settings);
                                    }, 2000)
                                })} />
                            </TabPanel>
                        </TabPane>
                    </div>
                </TabPanel>

                <TabPanel header={labels.manage_tournaments.title} >
                    <ManageTournaments host={host} profile={profile} />
                </TabPanel>

                <TabPanel header={labels.home.title} >
                    <Home hostWebsite={hostWebsite} host={host} profile={profile} />
                </TabPanel>

                <TabPanel header={labels.hero.title} >
                    <Gallery noCompound profile={profile} galleryState={hostWebsiteHeroState}
                        deleteLabel={i18nManager.Labels.account.profile_website.gallery.dialog.delete.delete_website_hero}
                        previewActionKey="action_label_1" previewLocationKey="action_location_1" previewCaptionKey="caption"
                        updateGalleryMethod={(hero) => hostService.updateHero.bind(hostService)(host.external_id, hero)} uploadPath="/omnehost/host/hero/"
                        enableRivtnAdState={galleryRivtnAdStateState} addGalleryMethod={(hero) => hostService.createHero.bind(hostService)(host.external_id, hero)}
                        deleteGalleryMethod={(hero) => hostService.deleteHero.bind(hostService)(host.external_id, hero)} enableAdLabel={labels.hero.enable_ad_label}
                        createLabel={i18nManager.Labels.account.profile_website.gallery.dialog.add_update.add_website_hero} updateLabel={i18nManager.Labels.account.profile_website.gallery.dialog.add_update.update_website_hero}
                        formControls={[
                            {
                                type: "file",
                                name: "media_location",
                            },
                            [
                                {
                                    type: "textbox",
                                    name: "action_label_1",
                                },
                                {
                                    type: "textbox",
                                    name: "action_location_1",
                                }
                            ],
                            [
                                {
                                    type: "textbox",
                                    name: "action_label_2",
                                },
                                {
                                    type: "textbox",
                                    name: "action_location_2",
                                }
                            ],
                            {
                                name: "caption",
                                type: "textarea",
                            },
                        ]}
                        onUnMount={() => {

                        }} fetchGallery={() => {
                            hostService.getHero(host.external_id).then(({ apiResponse }: SanitizedResponse<IGallery>) => {
                                hostWebsiteHeroState[1](apiResponse.data?.content);
                            }).catch(BaseService.reportError);
                        }} updateSettings={(enableRivtnAd: boolean) => {
                            galleryRivtnAdStateState[1](enableRivtnAd);
                            hostService.updateHostWebsite(host.external_id, { ...hostWebsite, hero_settings: { ...hostWebsite.hero_settings, enable_rivtn_ad: enableRivtnAd } }).then(({ }: SanitizedResponse<IHostWebsite>) => {
                                Siren.alert(labels.hero.update_successful);
                            }).catch(BaseService.reportError);
                        }} />
                </TabPanel>

                <TabPanel header={labels.merch.title} >
                    <Merch noCompound profile={profile} enableRivtnAdState={merchRivtnAdStateState} updateSettings={(enableRivtnAd: boolean) => {
                        merchRivtnAdStateState[1](enableRivtnAd);
                        hostService.updateHostWebsite(host.external_id, { ...hostWebsite, merchandise_settings: { ...hostWebsite.merchandise_settings, enable_rivtn_ad: enableRivtnAd } }).then(({ }: SanitizedResponse<IHostWebsite>) => {
                            Siren.alert(labels.merch.update_successful);
                        }).catch(BaseService.reportError);
                    }} />
                </TabPanel>

                <TabPanel header={labels.tournaments.title} >
                    <Tournaments host={host} hostWebsite={hostWebsite} profile={profile} />
                </TabPanel>

                <TabPanel header={labels.events.title} >
                    <Events host={host} hostWebsite={hostWebsite} profile={profile} />
                </TabPanel>

                <TabPanel header={labels.blog.title}>
                    <div className="te-settings-gen">
                        <TabPane renderActiveTabOnly noUnderline idleScheme={Schemes.IDLE} scheme={Schemes.OMNEHOST} alignment={width > 800 ? Alignment.LEFT : Alignment.TOP}
                            activeIndex={blogTabs.indexOf(searchParams.get("btab") ?? "CATEGORIES")} style={{ height: "100%" }}
                            attrsRelay={{ navigator: { headers: { style: { borderRight: "none", marginRight: 20, justifyContent: "flex-start" } } } }} onTabActive={(i) => {
                                Utils.updateSearchParams("btab", blogTabs[i], setSearchParams);
                            }}>
                            <TabPanel header={labels.blog.categories.title} attrsRelay={{ header: { style: { marginTop: (width > 800 ? 30 : 20) } } }}>
                                <Categories host={host} hostWebsite={hostWebsite} profile={profile} />
                            </TabPanel>
                            <TabPanel header={labels.blog.articles.title} attrsRelay={{ header: { style: { marginTop: 20 } } }}>
                                <Articles host={host} hostWebsite={hostWebsite} profile={profile} />
                            </TabPanel>
                            <TabPanel header={labels.blog.comment.title} attrsRelay={{ header: { style: { marginTop: 20 } } }}>
                                <Comments host={host} profile={profile} />
                            </TabPanel>
                        </TabPane>
                    </div>
                </TabPanel>

                <TabPanel header={labels.about.title} >
                    <div className="te-settings-gen">
                        <TabPane renderActiveTabOnly noUnderline idleScheme={Schemes.IDLE} scheme={Schemes.OMNEHOST} alignment={width > 800 ? Alignment.LEFT : Alignment.TOP}
                            activeIndex={aboutTabs.indexOf(searchParams.get("atab") ?? "VISIBILITIES")} style={{ height: "100%" }}
                            attrsRelay={{ navigator: { headers: { style: { borderRight: "none", marginRight: 20, justifyContent: "flex-start" } }, style: { width: (width > 800 ? undefined : width - 50) } }, }} onTabActive={(i) => {
                                Utils.updateSearchParams("atab", aboutTabs[i], setSearchParams);
                            }}>
                            <TabPanel header={labels.about.visibilities.title} attrsRelay={{ header: { style: { marginTop: (width > 800 ? 30 : 20) } } }}>
                                <Visibilities hostWebsite={hostWebsite} host={host} profile={profile} />
                            </TabPanel>
                            <TabPanel header={labels.about.company_info.title} attrsRelay={{ header: { style: { marginTop: 20 } } }}>
                                <CompanyInfo host={host} hostWebsite={hostWebsite} profile={profile} />
                            </TabPanel>
                            <TabPanel header={labels.about.phy_address.title} attrsRelay={{ header: { style: { marginTop: 20 } } }}>
                                <PhysicalAddress host={host} hostWebsite={hostWebsite} profile={profile} />
                            </TabPanel>
                            <TabPanel header={labels.about.email_phone.title} attrsRelay={{ header: { style: { marginTop: 20 } } }}>
                                <EmailPhone host={host} hostWebsite={hostWebsite} profile={profile} />
                            </TabPanel>
                            <TabPanel header={labels.about.people.title} attrsRelay={{ header: { style: { marginTop: 20 } } }}>
                                <People host={host} profile={profile} />
                            </TabPanel>
                        </TabPane>
                    </div>
                </TabPanel>

                <TabPanel header={labels.website.title} >
                    <Design host={host} hostWebsite={hostWebsite} profile={profile} />
                </TabPanel>

                <TabPanel header={labels.mobile_app.title} >
                    <MobileDesign host={host} profile={profile} />
                </TabPanel>
            </TabPane>)}
    </div>);

    function fetchInformation() {
        hostService.getHostByPublicId(hostId!).then(({ sanitized }: SanitizedResponse<IHost>) => {
            setHost(sanitized);
            hostService.getHostWebsite(sanitized.external_id).then(({ sanitized }: SanitizedResponse<IHostWebsite>) => {
                setHostWebsite(sanitized);
                galleryRivtnAdStateState[1](sanitized.hero_settings.enable_rivtn_ad);
                merchRivtnAdStateState[1](sanitized.merchandise_settings.enable_rivtn_ad);
            }).catch(BaseService.reportError);
        }).catch(BaseService.reportError);
    }

}

export default HostSettings;
